import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

export type GalleryImage = { id: number | string; url: string };

function GalleryLightbox({
  images,
  alt,
  openAt,
  onClose,
}: {
  images: GalleryImage[];
  alt: string;
  openAt: number;
  onClose: () => void;
}) {
  return (
    <Lightbox
      open={openAt >= 0}
      index={openAt}
      close={onClose}
      slides={images.map((image) => ({ src: image.url, alt }))}
      plugins={images.length > 1 ? [Zoom, Counter] : [Zoom]}
      carousel={{ finite: true }}
      controller={{ closeOnBackdropClick: true }}
      labels={{
        Previous: 'Poprzednie zdjęcie',
        Next: 'Następne zdjęcie',
        Close: 'Zamknij',
        'Zoom in': 'Powiększ',
        'Zoom out': 'Pomniejsz',
      }}
    />
  );
}

/**
 * Single product photo that opens the product's whole gallery full-screen.
 * Used where there is only room for one image — the checkout summary lines.
 */
export function ProductThumb({
  images,
  alt,
  fallback,
}: {
  images: GalleryImage[] | undefined;
  alt: string;
  fallback?: ReactNode;
}) {
  const [openAt, setOpenAt] = useState(-1);
  const slides = images ?? [];

  if (slides.length === 0) {
    return <div style={{ width: '100%', height: '100%' }}>{fallback}</div>;
  }

  return (
    <>
      <button
        type="button"
        className="product-gallery-slide"
        style={{ width: '100%', height: '100%' }}
        onClick={() => setOpenAt(0)}
        aria-label={
          slides.length > 1 ? `Powiększ zdjęcia (${slides.length})` : 'Powiększ zdjęcie'
        }
      >
        <img src={slides[0]!.url} alt={alt} />
      </button>
      <GalleryLightbox images={slides} alt={alt} openAt={openAt} onClose={() => setOpenAt(-1)} />
    </>
  );
}

/**
 * Horizontal strip of product photos that opens full-screen on click or tap.
 *
 * The strip is plain scroll-snap, not a carousel library: at two or three slides the
 * browser's own scrolling already is the feature, and a library would bring a theme to
 * fight with the shop's flat borders. The modal is not hand-rolled — focus trapping,
 * scroll locking, pinch-zoom and preloading are where this gets subtle.
 */
export function ProductGallery({
  images,
  alt,
  fallback,
}: {
  // Undefined is tolerated, not just empty: the catalogue is cached, so a browser running this
  // bundle can be handed a response serialised before galleries existed.
  images: GalleryImage[] | undefined;
  alt: string;
  fallback?: ReactNode;
}) {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const [openAt, setOpenAt] = useState(-1);

  const scrollTo = useCallback((index: number) => {
    const strip = stripRef.current;
    if (!strip) return;
    const left = strip.clientWidth * index;
    // Element.scrollTo is missing in jsdom and in WebKit before 15.4; scrollLeft is not.
    if (typeof strip.scrollTo === 'function') {
      strip.scrollTo({ left, behavior: 'smooth' });
    } else {
      strip.scrollLeft = left;
    }
    // A programmatic scroll fires no scroll event in those same engines, so the dots would
    // stay on the slide the buyer just left if we only listened.
    setActive(index);
  }, []);

  const onScroll = useCallback(() => {
    const strip = stripRef.current;
    if (!strip || strip.clientWidth === 0) return;
    setActive(Math.round(strip.scrollLeft / strip.clientWidth));
  }, []);

  const slides = images ?? [];

  if (slides.length === 0) {
    return <div style={{ width: '100%', height: '100%' }}>{fallback}</div>;
  }

  const multiple = slides.length > 1;

  // One photo is not a strip. A scroll container in the middle of the product page captures the
  // drag that starts on it, so with nothing to scroll sideways it just eats the page scroll.
  if (!multiple) {
    const only = slides[0]!;
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <button
          type="button"
          className="product-gallery-slide product-gallery-solo"
          onClick={() => setOpenAt(0)}
          aria-label="Powiększ zdjęcie"
        >
          <img src={only.url} alt={alt} />
        </button>
        <GalleryLightbox images={slides} alt={alt} openAt={openAt} onClose={() => setOpenAt(-1)} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <div
          ref={stripRef}
          className="product-gallery-strip"
          onScroll={onScroll}
        >
          {slides.map((image, i) => (
            <button
              key={image.id}
              type="button"
              className="product-gallery-slide"
              onClick={() => setOpenAt(i)}
              aria-label={`Powiększ zdjęcie ${i + 1} z ${slides.length}`}
            >
              <img src={image.url} alt={slides.length > 1 ? `${alt} — zdjęcie ${i + 1}` : alt} />
            </button>
          ))}
        </div>

        <button
          type="button"
          className="product-gallery-arrow"
          style={{ left: 0 }}
          onClick={() => scrollTo(Math.max(0, active - 1))}
          disabled={active === 0}
          aria-label="Poprzednie zdjęcie"
        >
          ‹
        </button>
        <button
          type="button"
          className="product-gallery-arrow"
          style={{ right: 0 }}
          onClick={() => scrollTo(Math.min(slides.length - 1, active + 1))}
          disabled={active === slides.length - 1}
          aria-label="Następne zdjęcie"
        >
          ›
        </button>
      </div>

      <div className="product-gallery-dots">
        {slides.map((image, i) => (
          <button
            key={image.id}
            type="button"
            className={i === active ? 'is-active' : undefined}
            onClick={() => scrollTo(i)}
            aria-label={`Zdjęcie ${i + 1}`}
            aria-current={i === active}
          />
        ))}
      </div>

      <GalleryLightbox images={slides} alt={alt} openAt={openAt} onClose={() => setOpenAt(-1)} />
    </div>
  );
}
